import {UI} from "../../../../../../stem-core/src/ui/UIBase";
import {StyleSheet} from "../../../../../../stem-core/src/ui/Style";

import {styleRule} from "../../../../../../stem-core/src/decorators/Style";
import {Messages} from "../../../../../Messages";
import {registerStyle} from "../../../../../../stem-core/src/ui/style/Theme";
import {iFrameState} from "../../../../../services/IFrameState";
import {Money} from "../../../../../../client/state/misc/Money";
import {MerchantCheckbox} from "../../../../../widget/ui/MerchantCheckbox";
import {LabeledCheckbox} from "../../../../../ui/Checkbox";
import {donateMobileState} from "../../../../inline-donation/DonateMobileState";
import {MERCHANT_SCROLLABLE_CONTENT_HEIGHT} from "../../../../../widget/WidgetConstants";
import {isSmallScreen} from "../../../../../UtilsLib";
import {DonateActiveButtonList} from "../../../../../common/donate/DonateActiveButtonList";
import {DonateAmountButtonList} from "../../../../../common/donate/DonateAmountButtonList";
import {queueMicrotaskWrapper} from "../../../../../AsyncAggregateDispatcher";
import {iFrameMerchantService} from "../../../../../services/IframeMerchantService";
import {iFrameUserDataService} from "../../../../../services/IFrameUserDataService";
import {autoredraw} from "../../../../../../stem-core/src/decorators/AutoRedraw";
import {TNR_ALIAS} from "../../../../../../blink-sdk/Constants";
import {MessageElement} from "../../../../../widget/ui/MessageElement";


export class DonateComponentStyle extends StyleSheet {
    @styleRule
    title = this.themeProps.INLINE_DONATE_FLOW_TITLE;

    @styleRule
    checkbox = this.themeProps.INLINE_DONATE_FLOW_CHECKBOX;

    @styleRule
    frequencyButtons = {
        marginTop: 18,
        marginBottom: 24,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL,
    };

    @styleRule
    scrollableOnMobile = {
        overflow: () => isSmallScreen() ? "auto" : "",
        maxHeight: () => isSmallScreen() ? MERCHANT_SCROLLABLE_CONTENT_HEIGHT : "",
        paddingRight: () => isSmallScreen() ? 18 : "",
        marginRight: () => isSmallScreen() ? -18 : "",
    };

    @styleRule
    customAmountError = {
        textAlign: "center",
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_SMALL,
        height: 15,
        color: this.themeProps.MERCHANT_ERROR
    };

    @styleRule
    donateAmountButtons = {
        marginBottom: 12,
    }
}

@autoredraw(iFrameUserDataService)
@registerStyle(DonateComponentStyle)
export class DonateComponent extends UI.Element {
    customInputError = "";
    donationIndex = null;
    defaultCustomAmount = null;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            feeCheckboxLabel: Messages.payDonationFeeCheckbox,
            DonateAmountButtonListClass: DonateAmountButtonList,
            DonateActiveButtonListClass: DonateActiveButtonList,
        }
    }

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.addClass(this.styleSheet.scrollableOnMobile);
    }

    formatAmount(amount) {
        return new Money(amount, iFrameMerchantService.getDonationOffer().getCurrency()).toString();
    }

    getDonationIndex() {
        const index = iFrameMerchantService.getDonationOffer().predefinedAmounts.indexOf(donateMobileState.getDonationAmount());
        return index === -1 ? iFrameMerchantService.getDonationOffer().predefinedAmounts.length : index;
    }

    handleDonationAmountChange(amount, isCustomAmount) {
        donateMobileState.donationAmount = amount;
        donateMobileState.isCustomAmount = isCustomAmount;
        this.dispatchChange();
        this.redraw();
    }

    handleFrequencyChange(frequency) {
        donateMobileState.frequency = frequency;
        this.dispatchChange();
        this.redraw();
    }

    // Some of these are called during renders and we want to make sure that the render is done by the time it is
    // dispatched, because the outer components might trigger another render during this dispatch.
    dispatchChange() {
        if (!this.changeDispatchEnqueued) {
            this.changeDispatchEnqueued = true;
            queueMicrotaskWrapper(() => {
                this.changeDispatchEnqueued = false;
                this.dispatch("change");
            });
        }
    }

    recurringOptionsAvailable() {
        return !iFrameUserDataService.getActiveRecurringDonation();
    }

    render() {
        const {styleSheet} = this;
        const {DonateAmountButtonListClass, DonateActiveButtonListClass, feeCheckboxLabel} = this.options;
        const offer = iFrameMerchantService.getDonationOffer();
        const {allowCustomAmount, predefinedAmounts, minAmount, maxAmount} = offer;
        const frequencyOptions = offer.getFrequencyOptions();

        if (this.donationIndex == null) {
            this.donationIndex = this.getDonationIndex();
        }
        const isCustomAmount = this.getDonationIndex() === predefinedAmounts.length;
        if (this.defaultCustomAmount == null) {
            if (isCustomAmount) {
                this.defaultCustomAmount = donateMobileState.donationAmount;
            } else {
                this.defaultCustomAmount = "";
            }
        } else if (!isCustomAmount) {
            this.defaultCustomAmount = "";
        }

        if (!donateMobileState.frequency && !this.recurringOptionsAvailable()) {
            donateMobileState.frequency = frequencyOptions[0];
            this.dispatchChange();
        }
        if (donateMobileState.donationAmount === "") {
            donateMobileState.donationAmount = donateMobileState.getDonationAmount();
            this.dispatchChange();
        }

        const onCustomInputError = (errorMessage) => {
            this.customInputError = errorMessage;
        };

        const onValidCustomInput = () => {
            this.customInputError = "";
            this.redraw();
        };

        const customAmountButtonOptions = {
            currency: iFrameMerchantService.getDonationOffer().getCurrency(),
            minAmount: minAmount,
            maxAmount: maxAmount,
            defaultCustomAmount: this.defaultCustomAmount,
        };

        const showFrequencies = this.recurringOptionsAvailable() && offer.hasFrequencyChoice();

        return [
            offer.hasAmountChoice() || showFrequencies || iFrameState.allowUserToCoverFees ?
                <div className={styleSheet.title}>{Messages.donationDetails}</div> : null,
            offer.hasAmountChoice() ?
            <DonateAmountButtonListClass defaultActiveButtonIndex={this.donationIndex}
                                         items={predefinedAmounts.map(amount => ({
                                                 label: this.formatAmount(amount),
                                                 value: amount,
                                             })
                                         )}
                                         className={styleSheet.donateAmountButtons}
                                         onCustomInputError={onCustomInputError}
                                         onValidCustomInput={onValidCustomInput}
                                         rowSizeLimit={isSmallScreen() ? 3 : 5}
                                         customAmountButtonOptions={allowCustomAmount ? customAmountButtonOptions : null}
                                         onSelect={({amount, isCustomAmount}) => this.handleDonationAmountChange(amount, isCustomAmount)}/>
                                         : null,
            allowCustomAmount ? <div className={styleSheet.customAmountError}>{this.customInputError}</div> : null,
            iFrameState.allowUserToCoverFees ?
                <LabeledCheckbox ref="payDonationFeeCheckbox" className={styleSheet.checkbox}
                                 testId="payDonationFeeCheckbox" CheckboxClass={MerchantCheckbox}
                                 initialValue={donateMobileState.payDonationFeeCheckboxChecked}
                                 label={feeCheckboxLabel(this.formatAmount(donateMobileState.getDonationFee()))}/>
                : null,
            iFrameMerchantService.isMerchant(TNR_ALIAS) ?
                <MessageElement message={Messages.tnrDonationMessage} /> : null,
            showFrequencies ?
                <DonateActiveButtonListClass defaultActiveButtonIndex={frequencyOptions.indexOf(donateMobileState.getFrequency())}
                                             className={styleSheet.frequencyButtons} rowSizeLimit={5}
                                             onSelect={(frequency) => this.handleFrequencyChange(frequency)}
                                             items={frequencyOptions.map(frequency => ({
                                                 label: frequency.getFrequencyName(),
                                                 value: frequency
                                             }))}/>
                : null,
        ];
    }

    addChangeListener(callback) {
        return this.addListener("change", callback);
    }

    removeChangeListener(callback) {
        return this.removeListener("change", callback);
    }

    onMount() {
        super.onMount();

        if (this.payDonationFeeCheckbox) {
            this.payDonationFeeCheckbox.addListener("toggle", ({enabled}) => {
                donateMobileState.payDonationFeeCheckboxChecked = enabled;
                this.dispatchChange();
            });
        }
    }
}
